/**
 * Elevation Gradient Service
 * 
 * Provides utilities for calculating and displaying elevation gradient levels
 * based on elevation gain per kilometer of distance.
 */

export interface ElevationGradientLevel {
  value: number
  perception: string
  color: string
}

// Elevation gradient scale (m/km) - standardized across the application
export const ELEVATION_GRADIENT_SCALE: ElevationGradientLevel[] = [
  { value: 0, perception: 'Flat', color: '#22c55e' }, // green-500
  { value: 1, perception: 'Rolling', color: '#eab308' }, // yellow-500
  { value: 2, perception: 'Hilly', color: '#f97316' }, // orange-500
  { value: 3, perception: 'Climby', color: '#ef4444' }, // red-500
  { value: 4, perception: 'Alpine', color: '#dc2626' } // red-600
]

/**
 * Calculate elevation gradient level (0-4) based on elevation gain and distance
 * @param elevationGain - Total elevation gain in meters
 * @param distance - Total distance in kilometers
 * @param routeType - Type of route ('single', 'bikepacking', etc.) for elevation penalty
 * @returns Gradient level (0-4)
 */
export const getElevationGradientLevel = (elevationGain: number, distance: number, routeType?: string): number => {
  if (distance <= 0 || elevationGain <= 0) return 0
  
  // Distance is in km, elevationGain is in meters
  // Gradient = meters of elevation gain per kilometer of distance
  const gradient = elevationGain / distance // m/km
  
  // Base level calculation with improved thresholds
  let level: number
  if (gradient < 10) level = 0 // Flat
  else if (gradient < 15) level = 1 // Rolling
  else if (gradient < 20) level = 2 // Hilly
  else if (gradient < 25) level = 3 // Climby
  else level = 4 // Alpine (25+ m/km)
  
  // Apply elevation penalty ONLY for single routes
  // This accounts for cumulative fatigue from total elevation
  if (routeType === 'single') {
    if (elevationGain > 5000) {
      // Massive elevation - boost up 2 levels (max Alpine)
      level = Math.min(4, level + 2)
    } else if (elevationGain > 4000) {
      // Big elevation - boost up 1 level
      level = Math.min(4, level + 1)
    } else if (elevationGain > 3000 && level < 2) {
      // Significant elevation - at least "Hilly"
      level = 2
    }
  }
  
  return level
}

/**
 * Get color for elevation gradient level
 * @param level - Gradient level (0-4)
 * @returns CSS color string
 */
export const getElevationGradientColor = (level: number): string => {
  return ELEVATION_GRADIENT_SCALE[level]?.color || ELEVATION_GRADIENT_SCALE[0].color
}

/**
 * Get human-readable label for elevation gradient level
 * @param level - Gradient level (0-4)
 * @returns Human-readable perception label
 */
export const getElevationGradientLabel = (level: number): string => {
  return ELEVATION_GRADIENT_SCALE[level]?.perception || 'Flat'
}

/**
 * Get complete elevation gradient info for a given level
 * @param level - Gradient level (0-4)
 * @returns Complete gradient level object
 */
export const getElevationGradientInfo = (level: number): ElevationGradientLevel => {
  return ELEVATION_GRADIENT_SCALE[level] || ELEVATION_GRADIENT_SCALE[0]
}

/**
 * Calculate and return complete gradient information for a route
 * @param elevationGain - Total elevation gain in meters
 * @param distance - Total distance in kilometers
 * @param routeType - Type of route ('single', 'bikepacking', etc.) for elevation penalty
 * @returns Object with level, color, label, and raw gradient value
 */
export const calculateRouteGradient = (elevationGain: number, distance: number, routeType?: string) => {
  const level = getElevationGradientLevel(elevationGain, distance, routeType)
  const gradient = distance > 0 ? elevationGain / distance : 0
  
  return {
    level,
    color: getElevationGradientColor(level),
    label: getElevationGradientLabel(level),
    gradient: Math.round(gradient * 10) / 10, // Round to 1 decimal place
    raw: {
      elevationGain,
      distance
    }
  }
}